import { useMemo } from "react";
import useSWR from "swr";
import { type DocumentVersionSummary } from "../types";
import { simpleFetcher } from "../fetcher";
import { type DocumentVersionStatus } from "~/config/types";
import { includesSearch } from "~/helpers/string";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";

export const QueryParams = {
  Title: "searchTitle",
  Status: "status",
};

const url = "/api/v2/document-versions";

interface Params {
  statuses?: DocumentVersionStatus[];
  titleSearch?: string | null;
  documentId?: number;
  tagIds?: number[];
  categoryIds?: number[];
  publishedBefore?: Date;
  includeWorkflow?: boolean;
}

export function useDocumentVersions({
  statuses,
  titleSearch,
  documentId,
  tagIds,
  categoryIds,
  publishedBefore,
  includeWorkflow,
}: Params = {}) {
  const isRefNumEnabled = useFeatureFlagEnabled(featureFlags.referenceNumber);
  const fetchUrl = includeWorkflow ? `${url}?includeWorkflow=true` : url;
  const swr = useSWR<DocumentVersionSummary[], Error>(fetchUrl, simpleFetcher);

  const filteredData = useMemo(
    () =>
      swr.data?.filter(
        (v) =>
          (!statuses || statuses.includes(v.status)) &&
          (!titleSearch ||
            includesSearch(v.title, titleSearch) ||
            (isRefNumEnabled &&
              includesSearch(v.document.referenceNumber ?? "", titleSearch))) &&
          (!documentId || v.document.id === documentId) &&
          (!tagIds?.length ||
            tagIds.some((tagId) => v.document.tagIds.includes(tagId))) &&
          (!categoryIds?.length ||
            categoryIds.some((dId) => v.document.category.id === dId)) &&
          (!publishedBefore ||
            (v.publishedAt && new Date(v.publishedAt) < publishedBefore)),
      ),
    [
      swr.data,
      statuses,
      titleSearch,
      isRefNumEnabled,
      documentId,
      tagIds,
      categoryIds,
      publishedBefore,
    ],
  );

  return {
    data: filteredData,
    error: swr.error,
    mutate: swr.mutate,
    isLoading: swr.isLoading,
  };
}
